import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Star, LogOut, Loader2, Search, Filter, Calendar, MessageSquare, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AdminReviews() {
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadTenants();
      loadReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant, selectedRating]);

  const loadTenants = async () => {
    try {
      const res = await adminApi.getTenants();
      setTenants(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadReviews = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (selectedTenant && selectedTenant !== 'all') params.tenant_id = selectedTenant;
      if (selectedRating && selectedRating !== 'all') params.rating = parseInt(selectedRating);
      
      const res = await adminApi.getReviews(params);
      setReviews(res.data.reviews);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const openReviewDetail = (review) => {
    setSelectedReview(review);
    setSheetOpen(true);
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= rating ? 'fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm opacity-80 hover:opacity-100">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm opacity-80 hover:opacity-100">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm font-medium">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm opacity-80 hover:opacity-100">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm opacity-80 hover:opacity-100">Gutscheine</Link>
            <Link to="/admin/meta-scores" className="text-sm opacity-80 hover:opacity-100">MetaScores</Link>
          </nav>
          <Button variant="ghost" size="icon" onClick={logout} className="text-primary-foreground hover:bg-white/20">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold">Bewertungen</h1>
            <p className="text-muted-foreground">{total} Bewertungen gesamt</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="w-48">
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger>
                <SelectValue placeholder="Alle Tenants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Tenants</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Select value={selectedRating} onValueChange={setSelectedRating}>
              <SelectTrigger>
                <SelectValue placeholder="Alle Bewertungen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Bewertungen</SelectItem>
                <SelectItem value="5">5 Sterne</SelectItem>
                <SelectItem value="4">4 Sterne</SelectItem>
                <SelectItem value="3">3 Sterne</SelectItem>
                <SelectItem value="2">2 Sterne</SelectItem>
                <SelectItem value="1">1 Stern</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card className="card-shadow">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">Keine Bewertungen gefunden</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Bewertung</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((review) => (
                    <TableRow key={review.id} className="cursor-pointer" onClick={() => openReviewDetail(review)}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {format(new Date(review.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{review.tenant_name}</p>
                      </TableCell>
                      <TableCell>{renderStars(review.overall_rating)}</TableCell>
                      <TableCell>
                        <Badge variant={review.path === 'positive' ? 'default' : 'secondary'}>
                          {review.path === 'positive' ? 'Positiv' : 'Kritik'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {review.reaction_gate?.chosen === 'send_to_hv' ? (
                          <Badge variant="outline" className="text-yellow-600">
                            Rückmeldung
                          </Badge>
                        ) : review.external?.target_platform ? (
                          <Badge variant="outline" className="text-green-600">
                            {review.external.target_platform}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Review Detail Sheet */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Bewertungsdetails</SheetTitle>
            </SheetHeader>
            {selectedReview && (
              <div className="mt-6 space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Tenant</p>
                  <p className="font-semibold">{selectedReview.tenant_name}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Gesamtbewertung</p>
                  <div className="flex items-center gap-2">
                    {renderStars(selectedReview.overall_rating)}
                    <span className="font-semibold">{selectedReview.overall_rating}/5</span>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground mb-1">Datum</p>
                  <p className="font-medium">
                    {format(new Date(selectedReview.createdAt), 'dd. MMMM yyyy, HH:mm', { locale: de })} Uhr
                  </p>
                </div>

                {selectedReview.categories?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Kategorien</p>
                    <div className="space-y-2">
                      {selectedReview.categories.map((cat, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm">{cat.label}</span>
                          {renderStars(cat.rating)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedReview.free_text && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Originaltext</p>
                    <p className="p-3 rounded-lg bg-muted text-sm">{selectedReview.free_text}</p>
                  </div>
                )}

                {selectedReview.rewritten_text && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Umformuliert</p>
                    <p className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
                      {selectedReview.rewritten_text}
                    </p>
                  </div>
                )}

                {selectedReview.fairness && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Fairness-Analyse</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.fairness_score}%</p>
                        <p className="text-xs text-muted-foreground">Sachlich</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.emotion_score}%</p>
                        <p className="text-xs text-muted-foreground">Emotion</p>
                      </div>
                      <div className="p-2 rounded bg-muted">
                        <p className="text-lg font-bold">{selectedReview.fairness.risk_score}%</p>
                        <p className="text-xs text-muted-foreground">Risiko</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedReview.verification?.email && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Kontakt</p>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedReview.verification.email}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </main>
    </div>
  );
}
